// Image Text Slider JavaScript
// File: assets/image-text-slider.js

document.addEventListener('DOMContentLoaded', function() {
  
  class ImageTextSlider {
    constructor(element) {
      this.element = element;
      this.slider = element.querySelector('.image-text-slider');
      this.flickityInstance = null;
      this.init();
    }

    init() {
      if (!this.slider) return;
      
      // Wait for images to load before initializing
      this.loadImages().then(() => {
        this.initFlickity();
        this.bindEvents();
      });
    }

    async loadImages() {
      const images = this.slider.querySelectorAll('img');
      const imagePromises = Array.from(images).map(img => {
        return new Promise((resolve, reject) => {
          if (img.complete) {
            resolve();
          } else {
            img.addEventListener('load', resolve);
            img.addEventListener('error', reject);
          }
        });
      });
      
      try {
        await Promise.all(imagePromises);
      } catch (error) {
        console.warn('Some images failed to load:', error);
      }
    }

    initFlickity() {
      // Get Flickity options from data attribute
      const flickityOptions = this.slider.getAttribute('data-flickity');
      let options = {};
      
      try {
        options = JSON.parse(flickityOptions);
      } catch (error) {
        console.warn('Invalid Flickity options:', error);
        options = this.getDefaultOptions();
      }

      // Initialize Flickity
      if (typeof Flickity !== 'undefined') {
        this.flickityInstance = new Flickity(this.slider, options);
        
        // Custom event handling
        this.flickityInstance.on('change', (index) => {
          this.onSlideChange(index);
        });
        
        this.flickityInstance.on('ready', () => {
          this.onSliderReady();
        });
      } else {
        console.error('Flickity library not loaded');
        this.fallbackSlider();
      }
    }

    getDefaultOptions() {
      return {
        cellAlign: 'left',
        contain: true,
        wrapAround: true,
        autoPlay: false,
        pauseAutoPlayOnHover: true,
        prevNextButtons: true,
        pageDots: true,
        draggable: true,
        freeScroll: false,
        accessibility: true,
        setGallerySize: true,
        resize: true
      };
    }

    onSlideChange(index) {
      // Add custom logic for slide change
      const slides = this.slider.querySelectorAll('.slider-cell');
      
      slides.forEach((slide, i) => {
        const content = slide.querySelector('.slide-content');
        if (i === index) {
          slide.classList.add('is-selected');
          content.style.animationDelay = '0.2s';
        } else {
          slide.classList.remove('is-selected');
          content.style.animationDelay = '0s';
        }
      });

      // Emit custom event
      this.element.dispatchEvent(new CustomEvent('slideChanged', {
        detail: { index, slide: slides[index] }
      }));
    }

    onSliderReady() {
      // Add ready class for styling
      this.element.classList.add('slider-ready');
      
      // Emit ready event
      this.element.dispatchEvent(new CustomEvent('sliderReady', {
        detail: { instance: this.flickityInstance }
      }));
    }

    bindEvents() {
      // Handle window resize
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (this.flickityInstance) {
            this.flickityInstance.resize();
          }
        }, 250);
      });

      // Handle visibility change (pause autoplay when tab is hidden)
      document.addEventListener('visibilitychange', () => {
        if (this.flickityInstance && this.flickityInstance.options.autoPlay) {
          if (document.hidden) {
            this.flickityInstance.pausePlayer();
          } else {
            this.flickityInstance.unpausePlayer();
          }
        }
      });

      // Keyboard navigation
      this.element.addEventListener('keydown', (e) => {
        if (!this.flickityInstance) return;
        
        switch(e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            this.flickityInstance.previous();
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.flickityInstance.next();
            break;
        }
      });

      // Touch/swipe gestures for mobile
      this.addTouchGestures();
    }

    addTouchGestures() {
      let startX = 0;
      let startY = 0;
      let isDragging = false;

      this.slider.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = false;
      });

      this.slider.addEventListener('touchmove', (e) => {
        if (!startX || !startY) return;
        
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        
        const deltaX = Math.abs(currentX - startX);
        const deltaY = Math.abs(currentY - startY);
        
        // If horizontal swipe is more significant than vertical
        if (deltaX > deltaY && deltaX > 30) {
          isDragging = true;
          e.preventDefault(); // Prevent scrolling
        }
      });

      this.slider.addEventListener('touchend', (e) => {
        if (!startX || !startY || !isDragging) return;
        
        const currentX = e.changedTouches[0].clientX;
        const deltaX = currentX - startX;
        
        if (Math.abs(deltaX) > 50 && this.flickityInstance) {
          if (deltaX > 0) {
            this.flickityInstance.previous();
          } else {
            this.flickityInstance.next();
          }
        }
        
        startX = 0;
        startY = 0;
        isDragging = false;
      });
    }

    fallbackSlider() {
      // Simple fallback if Flickity is not available
      console.warn('Using fallback slider implementation');
      
      const slides = this.slider.querySelectorAll('.slider-cell');
      const totalSlides = slides.length;
      let currentSlide = 0;
      
      if (totalSlides <= 1) return;
      
      // Hide all slides except first
      slides.forEach((slide, index) => {
        if (index !== 0) {
          slide.style.display = 'none';
        }
      });
      
      // Create navigation buttons
      const prevBtn = document.createElement('button');
      prevBtn.innerHTML = '‹';
      prevBtn.className = 'fallback-prev';
      
      const nextBtn = document.createElement('button');
      nextBtn.innerHTML = '›';
      nextBtn.className = 'fallback-next';
      
      this.element.appendChild(prevBtn);
      this.element.appendChild(nextBtn);
      
      const showSlide = (index) => {
        slides.forEach((slide, i) => {
          slide.style.display = i === index ? 'block' : 'none';
        });
        currentSlide = index;
      };
      
      prevBtn.addEventListener('click', () => {
        const newIndex = currentSlide === 0 ? totalSlides - 1 : currentSlide - 1;
        showSlide(newIndex);
      });
      
      nextBtn.addEventListener('click', () => {
        const newIndex = currentSlide === totalSlides - 1 ? 0 : currentSlide + 1;
        showSlide(newIndex);
      });
    }

    // Public methods
    next() {
      if (this.flickityInstance) {
        this.flickityInstance.next();
      }
    }

    previous() {
      if (this.flickityInstance) {
        this.flickityInstance.previous();
      }
    }

    goTo(index) {
      if (this.flickityInstance) {
        this.flickityInstance.select(index);
      }
    }

    destroy() {
      if (this.flickityInstance) {
        this.flickityInstance.destroy();
        this.flickityInstance = null;
      }
    }

    refresh() {
      if (this.flickityInstance) {
        this.flickityInstance.reloadCells();
        this.flickityInstance.resize();
      }
    }
  }

  // Auto-initialize all sliders on the page
  const initializeSliders = () => {
    const sliderElements = document.querySelectorAll('.image-text-slider-wrapper');
    
    sliderElements.forEach(element => {
      if (!element.dataset.initialized) {
        new ImageTextSlider(element);
        element.dataset.initialized = 'true';
      }
    });
  };

  // Initialize sliders
  initializeSliders();

  // Re-initialize when new content is loaded (for theme customizer)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', initializeSliders);
    document.addEventListener('shopify:section:reorder', initializeSliders);
  }

  // Expose utility functions globally
  window.ImageTextSlider = {
    init: initializeSliders,
    create: (element) => new ImageTextSlider(element)
  };
});

// CSS for fallback buttons
const fallbackStyles = `
  <style>
    .fallback-prev,
    .fallback-next {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      border: none;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      font-size: 18px;
      cursor: pointer;
      z-index: 10;
    }
    
    .fallback-prev {
      left: 20px;
    }
    
    .fallback-next {
      right: 20px;
    }
    
    .fallback-prev:hover,
    .fallback-next:hover {
      background: rgba(0, 0, 0, 1);
    }
  </style>
`;

document.head.insertAdjacentHTML('beforeend', fallbackStyles);